import { useCallback, useEffect, useMemo, useState } from "react";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import { TCustomerType } from "redux/reducers/models/facility";
import { sortObj } from "helpers/Helpers";

export function useBookingFeeCustomerTypes() {
  const [types, setTypes] = useState<TCustomerType[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadCustomerTypes(source.token);

    return () => {
      source.cancel();
    };
  }, []);

  async function loadCustomerTypes(token?: CancelToken) {
    setIsLoading(true);

    const res = await GetCustomerType({ application: "green_fee" }, token ? false : true, token);

    if (token?.reason) {
      return;
    }

    if (Number(res.status) !== Number(StatusCode.OK)) {
      setTypes([]);
      setIsLoading(false);
      return;
    }

    setTypes(res.data ?? []);
    setIsLoading(false);
  }

  const filteredTypes = useMemo(() => {
    if (!search) {
      return types;
    }

    const query = search.toLowerCase();

    return types.filter(
      type =>
        type?.full_title?.toLowerCase().includes(query) || type?.subtitle?.toLowerCase().includes(query),
    );
  }, [search, types]);

  const selectedTypes = useMemo(
    () => types.filter(type => type?.id != null && selectedIds.includes(type.id)),
    [selectedIds, types],
  );

  const typeById = useMemo(() => new Map(types.map(type => [type.id, type])), [types]);

  const selectAllFiltered = useCallback(() => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      filteredTypes.forEach(type => {
        if (type?.id != null) {
          next.add(type.id);
        }
      });
      return Array.from(next);
    });
  }, [filteredTypes]);

  const toggleSelectAllFiltered = useCallback(
    (checked: boolean) => {
      if (!checked) {
        const filteredIds = new Set(filteredTypes.map(type => type?.id).filter((id): id is number => id != null));
        setSelectedIds(prev => prev.filter(id => !filteredIds.has(id)));
        return;
      }

      selectAllFiltered();
    },
    [filteredTypes, selectAllFiltered],
  );

  const toggleType = useCallback((id: number) => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(value => value !== id) : [...prev, id]));
  }, []);

  const clearAll = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const reset = useCallback(() => {
    setSelectedIds([]);
    setSearch("");
  }, []);

  const setSelection = useCallback((ids: number[]) => {
    setSelectedIds(ids);
  }, []);

  const sortTypes = useCallback((key: keyof TCustomerType, direction: "asc" | "desc") => {
    setTypes(prev => sortObj(prev, key, direction));
  }, []);

  const allFilteredSelected =
    filteredTypes.length > 0 && filteredTypes.every(type => type?.id != null && selectedIds.includes(type.id));

  return {
    types,
    selectedIds,
    selectedTypes,
    search,
    setSearch,
    filteredTypes,
    typeById,
    isLoading,
    toggleType,
    toggleSelectAllFiltered,
    selectAllFiltered,
    clearAll,
    reset,
    setSelection,
    sortTypes,
    allFilteredSelected,
  };
}
